import { accountService } from "@/app/services/AccountService";
import { getWorkerServiceById, WorkerService } from "@/const/workerService";
import verifiedStatus from "@/models/verifiedStatus";
import { captureException } from "@/plugins/sentry";
import store from "@/store";
import { fcmMessageModule } from "@/store/fcm-message";
import { masterStore } from "@/store/master";
import { FirstBootSurvey } from "@/types/accounts/FirstBootSurvey";
import { FirstBootSurvey as FirstBootSurveyV2 } from "@/types/accounts/FirstBootSurveyV2";
import { ClosedTooltipPopupSelector } from "@/types/auth/ClosedTooltipPopup";
import { LoginStatus } from "@/types/auth/LoginStatus";
import { AuthInfo } from "@/types/AuthInfo";
import {
  Product,
  PRODUCT_SLUG_CAITECH_CARE,
  PRODUCT_SLUG_CAITECH_PHARMACIST
} from "@/types/Master";
import { EditableUserItems } from "@/types/user/EditableUserItems";
import { User } from "@/types/user/User";
import { getProductSlugFromUserAgent } from "@/utils/browserUtil";
import JwtDecode from "jwt-decode";
import * as uuid from "uuid";
import {
  Action,
  getModule,
  Module,
  Mutation,
  VuexModule
} from "vuex-module-decorators";
import {
  removeAdjustParameterUsername,
  setAdjustParameterUsername
} from "@/plugins/adjust";

// トークンを破棄するまでの猶予時間（秒）
export const TOKEN_DISCARDING_GRACE_TIME = 5 * 60;

@Module({
  dynamic: true,
  namespaced: true,
  store,
  name: "auth",
  preserveState: localStorage.getItem("vuex") !== null
})
class AuthModule extends VuexModule {
  token: string | null = null;
  user: User | null = null;

  currentProductSlug: string | null = null;

  // 認証前の電話番号を一時的に格納する変数
  temporaryPhoneNumber: string | null = null;
  smsSessionId: string | null = null;
  workerLoginStatus: string | null = null;
  // ローカルストレージが作られるたびに採番するID
  // ログイン前のユーザーに対して、一意のIDを付与するために使用する
  sessionId: string | null = null;

  // 初回アンケート
  firstBootSurvey: FirstBootSurvey | null = null;

  // 新初回アンケート(ABテストで使用)
  firstBootSurveyV2: FirstBootSurveyV2 | null = null;

  // 閉じた吹き出しポップアップのリスト
  closedTooltipPopupList: ClosedTooltipPopupSelector[] = [];

  // 閉じた吹き出しポップアップのリストが初期化済みか
  isInitializedClosedTooltipPopupList = false;

  segmentNumber3300?: number | null = null;

  segmentNumber3247?: number | null = null;
  // 次回応募時に理解度テストの回答を必須とするか
  shouldRequireUnderstandingTest? = false;

  segmentNumber2807?: number | null = null;

  segmentNumber3113?: number | null = null;

  segmentNumber3401?: number | null = null;

  // オンデバイス測定の初期設定が完了したかどうか
  initiatedODM = false;

  get product(): Product {
    return masterStore.products.find(
      p => p.slug === (this.currentProductSlug || PRODUCT_SLUG_CAITECH_CARE)
    )!;
  }

  get workerServiceOptionsByProduct() {
    return this.product.workerServices.map(ws => ({
      value: ws.id,
      text: ws.displayName
    }));
  }

  get isCaitechCareProduct() {
    return this.product.slug === PRODUCT_SLUG_CAITECH_CARE;
  }

  get isCaitechPharmacistProduct() {
    return this.product.slug === PRODUCT_SLUG_CAITECH_PHARMACIST;
  }

  get productAssetSuffix(): string {
    return this.isCaitechCareProduct ? "" : `-${this.product.slug}`;
  }

  get getPlatformStoreUrl() {
    return (
      platform: "iOS" | "Android",
      options?: {
        showReview?: boolean;
      }
    ) => {
      if (platform === "iOS") {
        const url = `https://apps.apple.com/us/app/id${this.product.iosAppId}`;
        return options?.showReview ? url + "?action=write-review" : url;
      } else if (platform === "Android") {
        return `https://play.google.com/store/apps/details?id=${this.product.androidAppId}`;
      }
      return "";
    };
  }

  get hasRegisteredPersonalInfo() {
    return this.user?.hasRegisteredPersonalInfo;
  }

  get hasRegisteredAvatar() {
    return this.user?.hasRegisteredAvatar;
  }

  get hasRegisteredIdentifier() {
    return this.user?.hasRegisteredIdentifier;
  }

  get hasVerifiedAccountInfo() {
    return this.user?.verifiedStatus === verifiedStatus.VERIFIED;
  }

  get hasRegisteredBusinessInfo() {
    if (!this.user?.experiences) {
      return false;
    }
    return this.user.experiences.length > 0;
  }

  // 初回認証申請を送信済みかどうか
  get hasRegisteredProfile() {
    return this.user?.hasRegisteredProfile;
  }

  get existToken() {
    return !!this.token;
  }

  get isNursing() {
    return this.workerService.id === WorkerService.NURSUKE.id;
  }

  get tokenObject() {
    if (this.token === null) {
      return null;
    }

    // TODO: 他にもkeyがあるが一旦有効期限だけ型付けしている
    return JwtDecode<{
      exp: number;
    }>(this.token);
  }

  get hasClosedTooltipPopup() {
    return (tooltipSelector: ClosedTooltipPopupSelector): boolean => {
      if (!this.isInitializedClosedTooltipPopupList) return false;
      return this.closedTooltipPopupList.includes(tooltipSelector);
    };
  }

  /**
   * token の期限切れまでの秒数取得
   * 関数にしないとDate.now()が走り直さずにキャッシュされてしまう
   */
  get tokenLifetime() {
    const tokenObject = this.tokenObject;
    if (!tokenObject) {
      return () => null;
    }

    // token の exp は Date.now() を数値化した値の秒数なので
    // 今のミリ秒を 1000 で割って算出する
    return () => {
      // eslint-disable-next-line no-restricted-syntax
      const current = Date.now().valueOf() / 1000;
      return +tokenObject.exp - current;
    };
  }

  /**
   * 有効期限が切れているかどうか
   * 関数にしないとtokenLifetime()が走り直さずにキャッシュされてしまう
   */
  get tokenIsExpired() {
    return () => {
      const lifetime = this.tokenLifetime();
      return lifetime === null || lifetime <= 0;
    };
  }

  /**
   * 有効期限が指定時間後に切れているかどうか
   * 関数にしないとtokenLifetime()が走り直さずにキャッシュされてしまう
   */
  get tokenIsNearExpired() {
    return () => {
      const lifetime = this.tokenLifetime();
      return lifetime === null || lifetime <= TOKEN_DISCARDING_GRACE_TIME;
    };
  }

  get workerService(): WorkerService {
    if (this.existToken && this.user && this.hasRegisteredPersonalInfo) {
      // 元々、this.user.workerServiceにはnameが入っていたが、idが入るように変更した。ただし、過渡期はstoreにnameが入ったままのケースがあるため両方動くように対応する
      if (
        // eslint-disable-next-line eqeqeq
        this.user.workerService == WorkerService.CAISUKE.id ||
        // @ts-ignore
        this.user.workerService === WorkerService.CAISUKE.serviceName
      ) {
        return WorkerService.CAISUKE;
      } else if (
        // eslint-disable-next-line eqeqeq
        this.user.workerService == WorkerService.NURSUKE.id ||
        // @ts-ignore
        this.user.workerService === WorkerService.NURSUKE.serviceName
      ) {
        return WorkerService.NURSUKE;
      } else if (this.user.workerService === WorkerService.PHARMACIST.id) {
        return WorkerService.PHARMACIST;
      } else {
        // ありえないケース
        throw new Error(
          `想定外の worker service: ${this.user.workerService} が指定されました。`
        );
      }
    } else {
      if (this.firstBootSurvey) {
        if (this.firstBootSurvey.qualification === "NURSING") {
          return WorkerService.NURSUKE;
        }
        if (this.firstBootSurvey.qualification === "PHARMACIST") {
          return WorkerService.PHARMACIST;
        }
        return WorkerService.CAISUKE;
      } else if (this.firstBootSurveyV2) {
        return getWorkerServiceById(this.firstBootSurveyV2.workerService);
      } else {
        return WorkerService.CAISUKE;
      }
    }
  }

  @Mutation
  private SET_CURRENT_PRODUCT_SLUG(productSlug: string) {
    this.currentProductSlug = productSlug;
  }

  @Mutation
  private SET_AUTH_INFO({ token, user }: AuthInfo) {
    this.token = token;
    this.user = user;
    // adjustのusernameをグローバルに設定
    setAdjustParameterUsername(user);
  }

  @Mutation
  private CLEAR_AUTH_INFO() {
    this.token = null;
    this.user = null;
    this.temporaryPhoneNumber = null;
    this.smsSessionId = null;
    this.workerLoginStatus = null;

    // adjustのusernameをグローバルから削除
    removeAdjustParameterUsername();
  }

  @Mutation
  private SET_NEW_TOKEN({ token }: Omit<AuthInfo, "user">) {
    this.token = token;
  }

  @Mutation
  private SET_USER(user: User) {
    this.user = user;
    // adjustのusernameをグローバルに設定
    setAdjustParameterUsername(user);
  }

  @Mutation
  private SET_PHONE_NUMBER(phoneNumber: string) {
    this.temporaryPhoneNumber = phoneNumber;
  }

  @Mutation
  private SET_SMS_SESSION_ID(smsSessionId: string) {
    this.smsSessionId = smsSessionId;
  }

  @Mutation
  private SET_WORKER_LOGIN_STATUS(workerLoginStatus: string) {
    this.workerLoginStatus = workerLoginStatus;
  }

  @Mutation
  private CLEAR_REGISTER_INFO() {
    this.temporaryPhoneNumber = null;
    this.smsSessionId = null;
    this.workerLoginStatus = null;
  }

  @Mutation
  private SET_FIRST_BOOT_SURVEY(survey: FirstBootSurvey) {
    this.firstBootSurvey = survey;
  }

  @Mutation
  private SET_FIRST_BOOT_SURVEY_V2(survey: FirstBootSurveyV2) {
    this.firstBootSurveyV2 = survey;
  }

  @Mutation
  private SET_CLOSED_TOOLTIP_POPUP_LIST(
    closedTooltipPopupList: ClosedTooltipPopupSelector[]
  ) {
    this.closedTooltipPopupList = closedTooltipPopupList;
  }

  @Mutation
  private ADD_CLOSED_TOOLTIP_POPUP(
    closedTooltipPopup: ClosedTooltipPopupSelector
  ) {
    // 重複を防ぐ処理
    if (!this.closedTooltipPopupList.includes(closedTooltipPopup)) {
      this.closedTooltipPopupList.push(closedTooltipPopup);
    }
  }

  @Mutation
  private SET_IS_INITIALIZED_CLOSED_TOOLTIP_POPUP_LIST(
    isInitializedClosedTooltipPopupList: boolean
  ) {
    this.isInitializedClosedTooltipPopupList = isInitializedClosedTooltipPopupList;
  }

  @Mutation
  private SET_SESSION_ID(sessionId: string) {
    this.sessionId = sessionId;
  }

  @Mutation
  private SET_SEGMENT_NUMBER_3300(segmentNumber: number) {
    this.segmentNumber3300 = segmentNumber;
  }

  @Mutation
  private SET_SEGMENT_NUMBER_3247(segmentNumber: number) {
    this.segmentNumber3247 = segmentNumber;
  }

  @Mutation
  private SET_SHOULD_REQUIRE_UNDERSTANDING_TEST(flag: boolean) {
    this.shouldRequireUnderstandingTest = flag;
  }

  @Mutation
  private SET_SEGMENT_NUMBER_2807(segmentNumber: number) {
    this.segmentNumber2807 = segmentNumber;
  }

  @Mutation
  private SET_SEGMENT_NUMBER_3113(segmentNumber: number) {
    this.segmentNumber3113 = segmentNumber;
  }

  @Mutation
  private SET_SEGMENT_NUMBER_3401(segmentNumber: number) {
    this.segmentNumber3401 = segmentNumber;
  }

  @Mutation
  private INITIATED_ODM() {
    this.initiatedODM = true;
  }

  @Action
  initializeCurrentProduct() {
    if (this.currentProductSlug) {
      return;
    }

    const productSlug = getProductSlugFromUserAgent();
    this.SET_CURRENT_PRODUCT_SLUG(productSlug);
  }

  @Action
  changeProduct(productSlug: string) {
    this.SET_CURRENT_PRODUCT_SLUG(productSlug);
  }

  @Action
  login(authInfo: AuthInfo) {
    this.SET_AUTH_INFO(authInfo);
    // 認証後にFCMトークンを送信
    fcmMessageModule.sendFcmToken();
  }

  @Action
  logout() {
    this.CLEAR_AUTH_INFO();
  }

  @Action
  refresh(newToken: Omit<AuthInfo, "user">) {
    this.SET_NEW_TOKEN(newToken);
  }

  @Action
  changeUserInfo(user: User) {
    this.SET_USER(user);
  }

  @Action
  changePhoneNumber(phoneNumber: string) {
    this.SET_PHONE_NUMBER(phoneNumber);
  }

  @Action
  changeSmsSessionId(smsSessionId: string) {
    this.SET_SMS_SESSION_ID(smsSessionId);
  }

  @Action
  changeWorkerLoginStatus(workerLoginStatus: LoginStatus) {
    this.SET_WORKER_LOGIN_STATUS(workerLoginStatus);
  }

  @Action
  clearRegisterInfo() {
    this.CLEAR_REGISTER_INFO();
  }

  @Action
  setFirstBootSurvey(survey: FirstBootSurvey) {
    this.SET_FIRST_BOOT_SURVEY(survey);
  }

  @Action
  setFirstBootSurveyV2(survey: FirstBootSurveyV2) {
    this.SET_FIRST_BOOT_SURVEY_V2(survey);
  }

  @Action
  async fetchFirstBootSurvey(fcmToken: string) {
    try {
      const survey = await accountService.fetchFirstBootSurvey(fcmToken);
      this.SET_FIRST_BOOT_SURVEY(survey);
    } catch (err) {
      if ((err as Error).message === "Request failed with status code 404") {
        return;
      }
      captureException(err as Error);
    }
  }

  @Action
  async fetchFirstBootSurveyV2(fcmToken: string) {
    try {
      const survey = await accountService.fetchFirstBootSurveyV2(fcmToken);
      this.SET_FIRST_BOOT_SURVEY_V2(survey);
    } catch (err) {
      captureException(err as Error);
    }
  }

  @Action
  async registerUserInfo(user: Partial<EditableUserItems>) {
    const res = await accountService.registerUserInformation(user);
    this.SET_USER(res);
  }

  @Action
  setClosedTooltipPopupList() {
    this.SET_IS_INITIALIZED_CLOSED_TOOLTIP_POPUP_LIST(true);
    this.SET_CLOSED_TOOLTIP_POPUP_LIST([]);
  }

  @Action
  addClosedTooltipPopup(closedTooltipPopup: ClosedTooltipPopupSelector) {
    this.ADD_CLOSED_TOOLTIP_POPUP(closedTooltipPopup);
  }

  @Action
  generateSessionId() {
    if (this.sessionId) return;
    this.SET_SESSION_ID(uuid.v4());
  }

  @Action
  setSegmentNumber3300(segmentNumber: number) {
    this.SET_SEGMENT_NUMBER_3300(segmentNumber);
  }

  @Action
  setSegmentNumber3247(segmentNumber: number) {
    this.SET_SEGMENT_NUMBER_3247(segmentNumber);
  }

  @Action
  setShouldRequireUnderstandingTest(flag: boolean) {
    this.SET_SHOULD_REQUIRE_UNDERSTANDING_TEST(flag);
  }

  @Action
  setSegmentNumber2807(segmentNumber: number) {
    this.SET_SEGMENT_NUMBER_2807(segmentNumber);
  }

  @Action
  setSegmentNumber3113(segmentNumber: number) {
    this.SET_SEGMENT_NUMBER_3113(segmentNumber);
  }

  @Action
  setSegmentNumber3401(segmentNumber: number) {
    this.SET_SEGMENT_NUMBER_3401(segmentNumber);
  }

  /**
   * オンデバイス測定の初期設定を行う
   * @param identifier 識別情報
   */
  @Action
  initiateOnDeviceConversionMeasurement(): void {
    // すでに処理済みの場合は何もしない
    if (this.initiatedODM) {
      return;
    }
    if (!this.user) {
      return;
    }
    if (
      // @ts-ignore
      window.FirebasePlugin === undefined ||
      // 関数がない場合（古いバージョンのアプリを利用しているなど）も何もしない
      // @ts-ignore
      typeof window.FirebasePlugin.initiateOnDeviceConversionMeasurement !==
        "function"
    ) {
      console.warn("skipped: initiateOnDeviceConversionMeasurement");
      return;
    }
    const identifier = (() => {
      if (this.user.email) {
        return {
          type: "emailAddress",
          value: this.user.email
        };
      }
      if (this.user.workerPhoneNumber) {
        return {
          type: "phoneNumber",
          value: this.user.workerPhoneNumber
        };
      }
      return null;
    })();
    if (!identifier) {
      return;
    }
    // @ts-ignore
    window.FirebasePlugin.initiateOnDeviceConversionMeasurement(
      { [identifier.type]: identifier.value },
      () => {
        console.log(
          `On device conversion measurement initiated, type: ${identifier.type}`
        );
        this.INITIATED_ODM();
      },
      (error: string) => {
        console.error(
          `Error initiating on device conversion measurement: ${error}`
        );
      }
    );
  }
}

export const authStore = getModule(AuthModule);
